import React from 'react';
import { Form as BaseForm } from '@economic/taco';
import FinalForm, { FormSpyProps } from 'react-final-form';
import useKeyShortcut from '../../hooks/useKeyShortcut';
import { calculateNextItemId } from './utils/CalculateNextItemId';
import AutoSave from './AutoSave';

export interface ArrowKeyNavigationProps<FormValues = unknown> {
    id?: string;
    validator: any;
    handleSubmit: (values: any) => Promise<{} | undefined>;
    collectionData?: any[];
    idKeyProp?: string;
    currentItemId?: string | number;
    onArrowKeySaveAndChange?: (nextItemId: string | number) => void;
    renderProps: FinalForm.FormRenderProps<FormValues>;
    className?: string;
    children: ((props: FinalForm.FormRenderProps<FormValues>) => React.ReactNode) | React.ReactNode;
    horizontal?: boolean;
    onChange?: FormSpyProps<FormValues>['onChange'];
}

const handleArrowKeySaveAndChange = async <FormValues,>(
    event: any,
    validator: any,
    handleSubmit: (values: any) => Promise<{}>,
    collectionData: any[],
    idKeyProp: string,
    currentItemId: string | number,
    isNext: boolean,
    renderProps: FinalForm.FormRenderProps<FormValues>,
    onArrowKeySaveAndChange: (nextItemId: string | number) => void
) => {
    event.target.blur();
    const validationErrors = await validator(renderProps.form.getState().values);
    if (Object.keys(validationErrors).length === 0) {
        const submitErrors = await handleSubmit(renderProps.form.getState().values);
        if (!submitErrors) {
            const nextItemId = calculateNextItemId(collectionData, idKeyProp, currentItemId, isNext);
            (onArrowKeySaveAndChange as (nextItemId: string | number) => void)(nextItemId);
        }
    }
};

export const BaseFormWithArrowKeyNavigation = <FormValues,>({
    id,
    collectionData,
    currentItemId,
    handleSubmit,
    idKeyProp,
    validator,
    onArrowKeySaveAndChange,
    renderProps,
    className,
    children,
    horizontal,
    onChange,
}: ArrowKeyNavigationProps<FormValues>) => {
    let upHandler: Function | undefined;
    let downHandler: Function | undefined;
    if (onArrowKeySaveAndChange) {
        upHandler = event =>
            handleArrowKeySaveAndChange(
                event,
                validator,
                handleSubmit as (values: any) => Promise<{}>,
                collectionData as any[],
                idKeyProp as string,
                currentItemId as string | number,
                false,
                renderProps,
                onArrowKeySaveAndChange
            );
        downHandler = event =>
            handleArrowKeySaveAndChange(
                event,
                validator,
                handleSubmit as (values: any) => Promise<{}>,
                collectionData as any[],
                idKeyProp as string,
                currentItemId as string | number,
                true,
                renderProps,
                onArrowKeySaveAndChange
            );
    }
    useKeyShortcut('ctrl+up', upHandler, undefined, true);
    useKeyShortcut('ctrl+down', downHandler, undefined, true);
    return (
        <>
            <BaseForm id={id} className={className} horizontal={horizontal} onSubmit={renderProps.handleSubmit}>
                {typeof children === 'function' ? children(renderProps) : children}
            </BaseForm>

            {onChange && <AutoSave subscription={{ valid: true, values: true, dirty: true }} onChange={onChange} />}
        </>
    );
};
